import { Maybe, StateAssert, TimeZoneId, ZonedDateTime } from '@csp/csp-common-model';
import { executeFunctionInChunks, isDefined } from '@csp/csp-common-util';
import { dropRightWhile, dropWhile, first, last, orderBy } from 'lodash';
import { isBefore, zonedMin } from '@csp/csp-common-date-util';
import { ActionTimestampWithValidity } from '../request-action/model/ActionTimestampWithValidity';
import { ScheduleReferenceDates } from '../model/ScheduleReferenceDates';
import { RequestWindow } from '../model/RequestWindow';
import { RequestWithVersionInfo } from '../request-info/model/RequestWithVersionInfo';
import { RolloutRequestResult } from '../model/RolloutRequestResult';
import { ScheduleVersionInfo } from '../model/schedulingModels/ScheduleVersionInfo';
import { ScheduleWindowInterval } from '../model/ScheduleWindowInterval';
import { GenericRequest } from '../model/schedulingModels/GenericRequest';
import { RequestAction } from '../model/schedulingModels/RequestAction';
import { RequestRef } from '../model/schedulingModels/RequestRef';
import { RequestActionDateUtil } from '../request-action/util/RequestActionDateUtil';
import { isValidStartAndEndAction, isWindowActiveNow } from '../util/scheduleUtil';
import { RequestTimingService } from '../timing/service/RequestTimingService';
import { TimingRepeatType } from '../model/schedulingModels/TimingRepeatType';
import { RequestWhileService } from './RequestWhileService';
import { ScheduleDateService } from './ScheduleDateService';
import { ScheduleIntervalService } from './ScheduleIntervalService';
import { ScheduleValidationService } from './ScheduleValidationService';

type RolloutRequestOptionsBase = {
  /** The request to roll out. */
  request: GenericRequest;

  /**
   * The user specific values that contains dates to pick start/end action from and
   * will be used to match against while criteria configuration.
   * Can be overriden by providing the {@link startActionTimestamp} arg.
   */
  scheduleReferenceDates?: ScheduleReferenceDates;

  /**
   * List of version infos where the request is active.
   */
  versionWindows?: ScheduleVersionInfo[];

  /** The timezone to roll out in. */
  timeZoneId?: TimeZoneId;
};

type RolloutRequestFromRepeatOptions = RolloutRequestOptionsBase & {
  /**
   * The initial date which the schedule calculation is based on,
   * typically extracted from the start action meta.
   */
  startActionTimestamp?: ZonedDateTime;
  /**
   * The end boundary of the request.
   * Limits the window end boundary and how many repeats should be done.
   */
  endActionTimestamp?: ZonedDateTime;

  /** The repeat index to start the roll out from. */
  fromRepeatIndex?: number;

  /** Maximum amount of repeats to calculate. */
  limitRepeat?: number;

  /** Provided to set request window validity to make it cleare which window to use when there are overlapping windows */
  validBetween?: ScheduleWindowInterval;
  /** The identifier of the source from where the start action was calculated */
  startActionSourceId?: string;
};

type AsyncRolloutOptions = {
  pauseIntervalMs?: number;
};

type AsyncRolloutRequestFromRepeatOptions = RolloutRequestFromRepeatOptions & AsyncRolloutOptions;

type RolloutRequestFromDateOptions = RolloutRequestOptionsBase & {
  /** The date start the roll out from. */
  fromDateInclusive: ZonedDateTime;

  /** Maximum amount of repeats to calculate. */
  limitRepeat: number;
};

type AsyncRolloutRequestFromDateOptions = RolloutRequestFromDateOptions & AsyncRolloutOptions;

type RolloutRequestBetweenDatesOptions = RolloutRequestOptionsBase & {
  /** The date start the roll out from. */
  fromDateInclusive: ZonedDateTime;

  /** The date to stop rollout on */
  toDateExclusive: ZonedDateTime;

  /** Maximum number of request windows to roll out. When this number is reached,
   * the rollout stops regardless if the toDate is reached  */
  maxAmountToRollout?: number;
};

type AsyncRolloutRequestBetweenDatesOptions = RolloutRequestBetweenDatesOptions & AsyncRolloutOptions;

type RolloutRequestRefOptions = {
  request: GenericRequest;
  requestRef: RequestRef;
  referenceDates: ScheduleReferenceDates;
  startActionSourceId: Maybe<string>;
};
type RolloutRequestRefResult = {
  hasChanged: boolean;
  newWindow: Maybe<RequestWindow>;
};

const rolloutRequestRef = async ({
  request,
  requestRef,
  referenceDates,
  startActionSourceId,
}: RolloutRequestRefOptions): Promise<RolloutRequestRefResult> => {
  const rollout = await rolloutRequest({
    request,
    scheduleReferenceDates: referenceDates,
    fromRepeatIndex: requestRef.repeatIndex,
    limitRepeat: 1,
    validBetween: undefined,
    startActionSourceId,
  });

  const requestWindow = rollout.requestWindows[0];

  const hasNotChanged =
    requestRef.windowStart?.unixTimeMillis === requestWindow?.windowStart.unixTimeMillis &&
    requestRef.windowEnd?.unixTimeMillis === requestWindow?.windowEnd?.unixTimeMillis;

  return {
    hasChanged: !hasNotChanged,
    newWindow: requestWindow,
  };
};

const rolloutRequest = async (options: AsyncRolloutRequestFromRepeatOptions): Promise<RolloutRequestResult> => {
  const { request, scheduleReferenceDates, versionWindows, validBetween, startActionSourceId } = options;
  const { isValidActions, startActionOffsetTimestamp, endActionOffsetTimestamp } = rolloutInfo(options);
  if (isValidActions) {
    const rolloutTimingResult = await RequestTimingService.rolloutTiming({
      timing: request.timing ?? {},
      startActionOffsetTimestamp: startActionOffsetTimestamp!,
      endActionOffsetTimestamp,
      limitRepeat: options.limitRepeat,
      fromRepeatIndex: options.fromRepeatIndex,
      pauseIntervalMs: options.pauseIntervalMs,
    });

    return RolloutRequestResult.fromTimingResult(
      rolloutTimingResult,
      request,
      scheduleReferenceDates,
      versionWindows,
      validBetween,
      startActionSourceId,
    );
  } else {
    return RolloutRequestResult.from([], request.timing.repeat.repeatType, false, false);
  }
};

const rolloutInfo = (
  options: RolloutRequestFromRepeatOptions,
): {
  isValidActions: boolean;
  startActionOffsetTimestamp: Maybe<ZonedDateTime>;
  endActionOffsetTimestamp: Maybe<ZonedDateTime>;
} => {
  ScheduleValidationService.validateRequest(options.request);

  const startActionOffsetTimestamp = getStartActionOffsetTimestamp(
    options.request,
    options.scheduleReferenceDates,
    options.startActionTimestamp,
    options.timeZoneId,
  );
  const endActionOffsetTimestamp = getEndActionOffsetTimestamp(
    options.request,
    options.scheduleReferenceDates,
    options.endActionTimestamp,
    options.timeZoneId,
  );

  const isValidActions = Boolean(
    startActionOffsetTimestamp && isValidStartAndEndAction(startActionOffsetTimestamp, endActionOffsetTimestamp),
  );

  return {
    startActionOffsetTimestamp,
    endActionOffsetTimestamp,
    isValidActions,
  };
};

type GetRepeatIndexFromDateArgs = {
  request: GenericRequest;
  startActionTimestamp: ZonedDateTime;
  dateTime: ZonedDateTime;
  timeZoneId: Maybe<TimeZoneId>;
};
const getHighestRepeatIndexOverlappingWithDate = ({
  dateTime,
  request,
  startActionTimestamp,
  timeZoneId,
}: GetRepeatIndexFromDateArgs): number =>
  RequestTimingService.getHighestRepeatIndexOverlappingWithDate({
    timing: request.timing ?? {},
    dateTime: timeZoneId ? ScheduleDateService.replaceTimezoneNameInZonedDate(dateTime, timeZoneId) : dateTime,
    startActionOffsetTimestamp: RequestActionDateUtil.applyActionOffsetAndFixedTime(
      request.startAction,
      startActionTimestamp,
      timeZoneId,
    ),
  });

const getLowestRepeatIndexOverlappingWithDate = ({
  dateTime,
  request,
  startActionTimestamp,
  timeZoneId,
}: GetRepeatIndexFromDateArgs): number =>
  RequestTimingService.getLowestRepeatIndexOverlappingWithDate({
    timing: request.timing ?? {},
    dateTime: timeZoneId ? ScheduleDateService.replaceTimezoneNameInZonedDate(dateTime, timeZoneId) : dateTime,
    startActionOffsetTimestamp: RequestActionDateUtil.applyActionOffsetAndFixedTime(
      request.startAction,
      startActionTimestamp,
      timeZoneId,
    ),
  });

type GetRolloutIntervalsOptions = {
  request: GenericRequest;
  /** The date start the roll out from. */
  fromDateInclusive: ZonedDateTime;

  /** The date to stop rollout on */
  toDateExclusive: Maybe<ZonedDateTime>;
  startActionTimestamp?: ZonedDateTime;
  endActionTimestamp?: ZonedDateTime;
  scheduleReferenceDates?: ScheduleReferenceDates;
  timeZoneId?: TimeZoneId;
};

type GetRolloutIntervalOptions = {
  request: GenericRequest;
  startActionTimestamp: ActionTimestampWithValidity;
  endActionTimestamps: ActionTimestampWithValidity[];
  /** The date start the roll out from. */
  fromDateInclusive: ZonedDateTime;

  /** The date to stop rollout on */
  toDateExclusive: Maybe<ZonedDateTime>;
  endActionTimestamp?: ZonedDateTime;
  scheduleReferenceDates?: ScheduleReferenceDates;
  timeZoneId?: TimeZoneId;
};

type GetRolloutIntervalOut = {
  startActionTimestamp: ZonedDateTime;
  endActionTimestamp: Maybe<ZonedDateTime>;
  rolloutInterval: ScheduleWindowInterval;
  validBetween: ScheduleWindowInterval;
  endActionOffsetTimestamp: Maybe<ZonedDateTime>;
  startActionSourceId: Maybe<string>;
};

const getRolloutInterval = ({
  request,
  startActionTimestamp: {
    actionTimestamp: startActionTimestamp,
    validBetween: startActionValidBetween,
    actionSourceId: startActionSourceId,
  },
  endActionTimestamps,
  fromDateInclusive,
  toDateExclusive,
  timeZoneId,
}: GetRolloutIntervalOptions): Maybe<GetRolloutIntervalOut> => {
  // Find all endActionTimestamps where the validity intersects with the startActionTimestamp
  const intersectingEndActionTimestamps = endActionTimestamps.filter(
    ({ validBetween: endActionValidBetween }) =>
      !!ScheduleIntervalService.intersection(startActionValidBetween, endActionValidBetween),
  );

  // Use the last intersecting endActionTimestamp
  const endActionTimestamp = orderBy(
    intersectingEndActionTimestamps,
    endActionTimestamp => endActionTimestamp.actionTimestamp.unixTimeMillis,
    'desc',
  )[0]?.actionTimestamp;

  // Make sure the interval is not larger than what is asked for
  const rolloutInterval = ScheduleIntervalService.intersection(startActionValidBetween, {
    windowStart: fromDateInclusive,
    windowEnd: toDateExclusive,
  });

  // Apply offset and fixed time
  const endActionOffsetTimestamp = endActionTimestamp
    ? RequestActionDateUtil.applyActionOffsetAndFixedTime(request.endAction, endActionTimestamp, timeZoneId)
    : undefined;

  return rolloutInterval
    ? {
        startActionTimestamp,
        rolloutInterval,
        endActionTimestamp,
        validBetween: startActionValidBetween,
        endActionOffsetTimestamp,
        startActionSourceId,
      }
    : undefined;
};

const getRolloutIntervals = ({
  request,
  scheduleReferenceDates,
  fromDateInclusive,
  toDateExclusive,
  timeZoneId,
}: GetRolloutIntervalsOptions): GetRolloutIntervalOut[] => {
  const startActionTimestamps = getStartActionTimestampHistory(request, scheduleReferenceDates, timeZoneId);
  const endActionTimestamps = getEndActionTimestampHistory(request, scheduleReferenceDates, timeZoneId);

  return startActionTimestamps
    .map(startActionTimestamp =>
      getRolloutInterval({
        request,
        startActionTimestamp,
        endActionTimestamps,
        fromDateInclusive,
        toDateExclusive,
        timeZoneId,
      }),
    )
    .filter(isDefined);
};

/**
 * Calculates the windows of a request from a given date.
 *
 * @throws Will throw an error if not valid request.
 */
const rolloutRequestFromDate = async ({
  request,
  scheduleReferenceDates,
  fromDateInclusive,
  limitRepeat,
  versionWindows,
  timeZoneId,
  pauseIntervalMs,
}: AsyncRolloutRequestFromDateOptions): Promise<RolloutRequestResult> => {
  ScheduleValidationService.validateRequest(request);

  if (timeZoneId) {
    fromDateInclusive = ScheduleDateService.replaceTimezoneNameInZonedDate(fromDateInclusive, timeZoneId);
  }

  const rolloutIntervals = getRolloutIntervals({
    request,
    scheduleReferenceDates,
    fromDateInclusive,
    toDateExclusive: undefined,
    timeZoneId,
  });

  const isApplicableNow = isRequestApplicableNow(request, scheduleReferenceDates);
  const rolloutResult = RolloutRequestResult.from([], request.timing.repeat.repeatType, true, isApplicableNow);

  for (const {
    rolloutInterval,
    startActionTimestamp,
    endActionTimestamp,
    validBetween,
    endActionOffsetTimestamp,
    startActionSourceId,
  } of rolloutIntervals) {
    const maxRepeatsToRollout = limitRepeat - rolloutResult.requestWindows.length;
    if (maxRepeatsToRollout <= 0) {
      break;
    }
    const fromIndex = getLowestRepeatIndexOverlappingWithDate({
      request,
      dateTime: rolloutInterval.windowStart,
      startActionTimestamp,
      timeZoneId,
    });

    const partialRequestRollout = await rolloutRequest({
      request,
      startActionTimestamp,
      endActionTimestamp,
      scheduleReferenceDates,
      fromRepeatIndex: fromIndex,
      versionWindows,
      timeZoneId,
      limitRepeat: maxRepeatsToRollout,
      validBetween,
      pauseIntervalMs,
      startActionSourceId,
    });

    getFilteredWindows(
      partialRequestRollout.requestWindows,
      rolloutInterval.windowStart,
      rolloutInterval.windowEnd,
      endActionOffsetTimestamp,
    ).forEach(filteredWindow => rolloutResult.requestWindows.push(filteredWindow));
  }

  rolloutResult.requestWindows = getFilteredWindows(
    rolloutResult.requestWindows,
    fromDateInclusive,
    undefined,
    undefined,
  );
  return rolloutResult;
};

const getFilteredWindows = (
  requestWindows: RequestWindow[],
  fromDateInclusive: ZonedDateTime,
  toDateExclusive: Maybe<ZonedDateTime>,
  endActionOffsetTimeStamp: Maybe<ZonedDateTime>,
): RequestWindow[] => {
  const earliestDate = zonedMin([endActionOffsetTimeStamp, toDateExclusive].filter(isDefined));

  const leadingWindowsFiltered = dropWhile(
    requestWindows,
    window =>
      !ScheduleIntervalService.intersection(window, { windowStart: fromDateInclusive, windowEnd: earliestDate }),
  );

  const leadingAndTrailingWindowsFiltered = dropRightWhile(
    leadingWindowsFiltered,
    window =>
      !ScheduleIntervalService.intersection(window, { windowStart: fromDateInclusive, windowEnd: earliestDate }),
  );

  return leadingAndTrailingWindowsFiltered;
};

const shouldDoPartialRollout = (indexesBetween: number, repeatType: TimingRepeatType): boolean =>
  indexesBetween > 0 || repeatType === TimingRepeatType.NONE;

type RolloutIntervalInfoOut = {
  fromIndex: number;
  limitRepeat: number;
  indexesBetween: number;
};

type RolloutBetweenDatesInfoOut = {
  rolloutIntervals: GetRolloutIntervalOut[];
  isApplicableNow: boolean;
  fromDateInclusive: ZonedDateTime;
  toDateExclusive: ZonedDateTime;
  rolloutIntervalInfo: (
    rolloutInterval: ScheduleWindowInterval,
    startActionTimestamp: ZonedDateTime,
    endActionTimestamp: Maybe<ZonedDateTime>,
    maxLeftToRollout: Maybe<number>,
  ) => RolloutIntervalInfoOut;
};

const rolloutBetweenDatesInfo = (options: RolloutRequestBetweenDatesOptions): RolloutBetweenDatesInfoOut => {
  const { request, timeZoneId } = options;
  ScheduleValidationService.validateRequest(request);

  let fromDateInclusive = options.fromDateInclusive;
  let toDateExclusive = options.toDateExclusive;
  if (timeZoneId) {
    fromDateInclusive = ScheduleDateService.replaceTimezoneNameInZonedDate(fromDateInclusive, timeZoneId);
    toDateExclusive = ScheduleDateService.replaceTimezoneNameInZonedDate(toDateExclusive, timeZoneId);
  }

  const rolloutIntervals = getRolloutIntervals({
    request,
    scheduleReferenceDates: options.scheduleReferenceDates,
    fromDateInclusive,
    toDateExclusive,
    timeZoneId,
  });

  const isApplicableNow = isRequestApplicableNow(request, options.scheduleReferenceDates);

  /** Helper function to get relevant info from a rollout schedule interval */
  const rolloutIntervalInfo = (
    rolloutInterval: ScheduleWindowInterval,
    startActionTimestamp: ZonedDateTime,
    endActionTimestamp: Maybe<ZonedDateTime>,
    maxLeftToRollout: Maybe<number>,
  ): RolloutIntervalInfoOut => {
    const fromIndex = getLowestRepeatIndexOverlappingWithDate({
      request,
      dateTime: rolloutInterval.windowStart,
      startActionTimestamp,
      timeZoneId: options.timeZoneId,
    });

    const rolloutIntervalEnd = rolloutInterval.windowEnd;
    StateAssert.isDefined(rolloutIntervalEnd, 'Unexpected undefined "windowEnd"');

    const endActionOffsetTimestamp = endActionTimestamp
      ? RequestActionDateUtil.applyActionOffsetAndFixedTime(request.endAction, endActionTimestamp, timeZoneId)
      : undefined;

    // Use the earliest date get a correct toIndex for the interval
    const earliestDate = zonedMin([rolloutIntervalEnd, endActionOffsetTimestamp]);

    const toIndex = getHighestRepeatIndexOverlappingWithDate({
      request,
      dateTime: earliestDate,
      startActionTimestamp,
      timeZoneId: options.timeZoneId,
    });

    const indexesBetween = toIndex - fromIndex;

    const limitRepeat = maxLeftToRollout !== undefined ? Math.min(indexesBetween, maxLeftToRollout) : indexesBetween;

    return {
      fromIndex,
      limitRepeat,
      indexesBetween,
    };
  };

  return {
    rolloutIntervalInfo,
    rolloutIntervals,
    isApplicableNow,
    fromDateInclusive,
    toDateExclusive,
  };
};

const rolloutRequestBetweenDates = async (
  options: AsyncRolloutRequestBetweenDatesOptions,
): Promise<RolloutRequestResult> => {
  const { request, scheduleReferenceDates, versionWindows, timeZoneId, maxAmountToRollout, pauseIntervalMs } = options;

  const { rolloutIntervalInfo, rolloutIntervals, isApplicableNow, fromDateInclusive, toDateExclusive } =
    rolloutBetweenDatesInfo(options);

  const rolloutResult = RolloutRequestResult.from(
    [],
    request.timing.repeat.repeatType,
    !!rolloutIntervals.length,
    isApplicableNow,
  );

  let maxLeftToRollout = maxAmountToRollout;

  for (const {
    startActionTimestamp,
    rolloutInterval,
    endActionTimestamp,
    validBetween,
    endActionOffsetTimestamp,
    startActionSourceId,
  } of rolloutIntervals) {
    const { fromIndex, indexesBetween, limitRepeat } = rolloutIntervalInfo(
      rolloutInterval,
      startActionTimestamp,
      endActionTimestamp,
      maxLeftToRollout,
    );

    StateAssert.isDefined(rolloutInterval.windowEnd, 'Unexpected undefined "windowEnd"');
    const repeatType = request.timing.repeat.repeatType;
    const doPartialRollout = shouldDoPartialRollout(indexesBetween, repeatType);

    if (doPartialRollout) {
      const partialRequestRollout = await rolloutRequest({
        request,
        startActionTimestamp,
        endActionTimestamp,
        scheduleReferenceDates,
        fromRepeatIndex: fromIndex,
        versionWindows,
        timeZoneId,
        limitRepeat,
        validBetween,
        pauseIntervalMs,
        startActionSourceId,
      });

      getFilteredWindows(
        partialRequestRollout.requestWindows,
        rolloutInterval.windowStart,
        rolloutInterval.windowEnd,
        endActionOffsetTimestamp,
      ).forEach(filteredWindow => rolloutResult.requestWindows.push(filteredWindow));

      rolloutResult.hasLimitedRequestWindows = limitRepeat < indexesBetween;
      maxLeftToRollout = maxAmountToRollout ? maxAmountToRollout - rolloutResult.requestWindows.length : undefined;
    }
  }

  rolloutResult.requestWindows = getFilteredWindows(
    rolloutResult.requestWindows,
    fromDateInclusive,
    toDateExclusive,
    undefined,
  );
  return rolloutResult;
};

const getActionOffsetTimestamp = (
  requestAction: Maybe<RequestAction>,
  scheduleReferenceDates: Maybe<ScheduleReferenceDates>,
  actionTimestampOverride: Maybe<ZonedDateTime>,
  timeZoneId: Maybe<TimeZoneId>,
): Maybe<ZonedDateTime> => {
  if (actionTimestampOverride) {
    return RequestActionDateUtil.applyActionOffsetAndFixedTime(requestAction, actionTimestampOverride, timeZoneId);
  } else if (scheduleReferenceDates && requestAction) {
    return RequestActionDateUtil.getActionOffsetTimestamp(requestAction, scheduleReferenceDates, timeZoneId);
  } else {
    return undefined;
  }
};

const getStartActionTimestampHistory = (
  request: GenericRequest,
  scheduleReferenceDates: Maybe<ScheduleReferenceDates>,
  timeZoneId: Maybe<TimeZoneId>,
): ActionTimestampWithValidity[] => {
  if (scheduleReferenceDates && request.startAction) {
    return RequestActionDateUtil.getRequestActionTimestamps(request.startAction, scheduleReferenceDates, timeZoneId);
  } else {
    return [];
  }
};

const getEndActionTimestampHistory = (
  request: GenericRequest,
  scheduleReferenceDates: Maybe<ScheduleReferenceDates>,
  timeZoneId: Maybe<TimeZoneId>,
): ActionTimestampWithValidity[] => {
  if (scheduleReferenceDates && request.endAction) {
    return RequestActionDateUtil.getRequestActionTimestamps(request.endAction, scheduleReferenceDates, timeZoneId);
  } else {
    return [];
  }
};

const getStartActionOffsetTimestamp = (
  request: GenericRequest,
  scheduleReferenceDates: Maybe<ScheduleReferenceDates>,
  startActionTimestampOverride: Maybe<ZonedDateTime>,
  timeZoneId: Maybe<TimeZoneId>,
): Maybe<ZonedDateTime> =>
  getActionOffsetTimestamp(request.startAction, scheduleReferenceDates, startActionTimestampOverride, timeZoneId);

const getEndActionOffsetTimestamp = (
  request: GenericRequest,
  scheduleReferenceDates: Maybe<ScheduleReferenceDates>,
  endActionTimestampOverride: Maybe<ZonedDateTime>,
  timeZoneId: Maybe<TimeZoneId>,
): Maybe<ZonedDateTime> =>
  getActionOffsetTimestamp(request.endAction, scheduleReferenceDates, endActionTimestampOverride, timeZoneId);

const isRequestApplicableNow = (
  request: GenericRequest,
  scheduleReferenceDates: Maybe<ScheduleReferenceDates>,
): boolean => {
  const isWithinMatchingIntervalNow = scheduleReferenceDates
    ? RequestWhileService.isWithinMatchingIntervalNow(request, scheduleReferenceDates)
    : true;

  return isWithinMatchingIntervalNow;
};

type CompareRequestRolloutsOptions = {
  request: RequestWithVersionInfo;
  oldReferenceDates: ScheduleReferenceDates;
  newReferenceDates: ScheduleReferenceDates;
  timeZoneId: TimeZoneId;
  fromDateInclusive: ZonedDateTime;
  toDateExclusive: ZonedDateTime;
};
type CompareRequestRollouts = {
  isChanged: boolean;
  addedRequestWindows: RequestWindow[]; // Will be missed
};

const compareRequestRolloutsBetweenDates = async ({
  request,
  oldReferenceDates,
  newReferenceDates,
  fromDateInclusive,
  toDateExclusive,
  timeZoneId,
}: CompareRequestRolloutsOptions): Promise<CompareRequestRollouts> => {
  const oldResultPromise = rolloutRequestBetweenDates({
    request,
    fromDateInclusive,
    toDateExclusive,
    scheduleReferenceDates: oldReferenceDates,
    timeZoneId,
    versionWindows: request.versions,
  });
  const newResultPromise = rolloutRequestBetweenDates({
    request,
    fromDateInclusive,
    toDateExclusive,
    scheduleReferenceDates: newReferenceDates,
    timeZoneId,
    versionWindows: request.versions,
  });
  const [oldResult, newResult] = await Promise.all([oldResultPromise, newResultPromise]);

  const oldWindowsByRepeatIndex = new Map<string, RequestWindow>(
    await executeFunctionInChunks(50, oldResult.requestWindows, windows =>
      windows.map(window => [RequestWindow.toKey(window), window]),
    ),
  );

  const addedRequestWindows = await executeFunctionInChunks(50, newResult.requestWindows, windows =>
    windows.filter(newWindow => !oldWindowsByRepeatIndex.get(RequestWindow.toKey(newWindow))),
  );

  return {
    isChanged: addedRequestWindows.length > 0,
    addedRequestWindows,
  };
};

type UserOngoingIntervals = {
  intervals: ScheduleWindowInterval[];
  firstStartToLastEnd: Maybe<ScheduleWindowInterval>;
};

const getUserOngoingIntervals = (
  requestWithVersionInfo: RequestWithVersionInfo,
  scheduleReferenceDates: ScheduleReferenceDates,
): UserOngoingIntervals => {
  const startActionOffsetTimestamp = RequestActionDateUtil.getStartActionOffsetTimestamp(
    requestWithVersionInfo,
    scheduleReferenceDates,
  );
  const endActionOffsetTimestamp = RequestActionDateUtil.getEndActionOffsetTimestamp(
    requestWithVersionInfo,
    scheduleReferenceDates,
  );
  const matchingWhileIntervals =
    requestWithVersionInfo.while &&
    RequestWhileService.getMatchingIntervals(requestWithVersionInfo.while, scheduleReferenceDates);

  const allVersionIntervals: ScheduleWindowInterval[] = orderBy(
    requestWithVersionInfo.versions,
    info => info.activeFrom.unixTimeMillis,
  ).map(info => ({ windowStart: info.activeFrom, windowEnd: info.activeTo }));

  if (startActionOffsetTimestamp) {
    const startEndActionInterval: ScheduleWindowInterval = {
      windowStart: startActionOffsetTimestamp,
      windowEnd: endActionOffsetTimestamp,
    };
    const userRequestIntervals = matchingWhileIntervals
      ? ScheduleIntervalService.intersectionOfIntervalLists([startEndActionInterval], matchingWhileIntervals)
      : [startEndActionInterval];

    const allOngoingIntervals = ScheduleIntervalService.intersectionOfIntervalLists(
      allVersionIntervals,
      userRequestIntervals,
    );

    const firstInterval = first(allOngoingIntervals);

    // Get last window end of the interval that is either active or has been passed most recently.
    let lastWindowEnd: Maybe<ZonedDateTime> = last(allOngoingIntervals)?.windowEnd;
    const activeWindow = allOngoingIntervals.find(window => isWindowActiveNow(window));
    const lastPassedWindow = allOngoingIntervals.findLast(({ windowEnd }) =>
      windowEnd ? isBefore(windowEnd, new Date()) : false,
    );
    if (activeWindow || lastPassedWindow) {
      lastWindowEnd = activeWindow ? activeWindow.windowEnd : lastPassedWindow?.windowEnd;
    }

    return {
      intervals: allOngoingIntervals,
      firstStartToLastEnd: firstInterval
        ? {
            windowStart: firstInterval.windowStart,
            windowEnd: lastWindowEnd,
          }
        : undefined,
    };
  } else {
    return {
      intervals: [],
      firstStartToLastEnd: undefined,
    };
  }
};

export const SchedulingService = {
  rolloutRequest,
  rolloutRequestBetweenDates,
  rolloutRequestFromDate,
  rolloutRequestRef,
  compareRequestRolloutsBetweenDates,
  getUserOngoingIntervals,
};
